import Highcharts from '~/scripts/integrations/highcharts';

class PointsBudgetCalculator {
  constructor() {
    this.form = document.getElementById('calculator-form');
    this.employeeCountInput = document.getElementById('employee-count');
    this.redemptionRateInput = document.getElementById('redemption-rate');
    this.pointsRatioInput = document.getElementById('points-ratio');
    this.programDurationInput = document.getElementById('program-duration');
    this.budgetAmount = document.getElementById('budget-amount');
    this.budgetText = document.getElementById('budget-text');

    // Chart instances
    this.roleBudgetChart = null;
    this.anniversaryChart = null;

    this.resetBtn = document.getElementById('reset-points-calculator');

    this.bindEvents();
    this.updateAnniversaryCosts();
    this.calculateBudget();
  }

  bindEvents() {
    // Basic form inputs
    [
      this.employeeCountInput,
      this.redemptionRateInput,
      this.pointsRatioInput,
      this.programDurationInput
    ].forEach(input => {
      input.addEventListener('input', () => this.calculateBudget());
      input.addEventListener('change', () => this.calculateBudget());
    });

    // Role management
    document.getElementById('add-role').addEventListener('click', () => this.addRole());
    document.addEventListener('click', (e) => {
      if (e.target.closest('.remove-role')) {
        this.removeRole(e.target.closest('.role-budget-row'));
      }
    });

    // Anniversary management
    document.getElementById('add-anniversary').addEventListener('click', () => this.addAnniversary());
    document.addEventListener('click', (e) => {
      if (e.target.closest('.remove-anniversary')) {
        e.target.closest('.row').remove();
        this.calculateBudget();
      }
    });

    // Dynamic inputs for roles and anniversaries
    document.addEventListener('input', (e) => {
      if (e.target.closest('#role-budgets') || e.target.closest('#anniversary-points')) {
        this.calculateBudget();
      }
      // Update totals when anniversary percentages change
      if (e.target.matches('[data-anniversary-percentage]')) {
        this.updateAnniversaryEmployeeCounts();
      }
    });

    // Update anniversary costs and employee counts when points ratio or employee count changes
    this.pointsRatioInput.addEventListener('input', () => {
      this.updateAnniversaryCosts();
    });

    this.employeeCountInput.addEventListener('input', () => {
      this.updateAnniversaryEmployeeCounts();
    });

    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', () => this.reset());
    }
  }

  addRole() {
    const roleBudgets = document.getElementById('role-budgets');
    const newRole = document.createElement('div');
    newRole.className = 'row mb-2 role-budget-row';
    newRole.innerHTML = `
      <div class="col-md-4">
        <input type="text" class="form-control" placeholder="Role name" data-role-name>
      </div>
      <div class="col-md-3">
        <input type="number" class="form-control" placeholder="Count" value="0" min="0" data-role-count>
      </div>
      <div class="col-md-3">
        <input type="number" class="form-control" placeholder="Points/month" value="100" min="0" data-role-points>
      </div>
      <div class="col-md-2">
        <button type="button" class="btn btn-outline-danger btn-sm remove-role">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    `;
    roleBudgets.appendChild(newRole);
  }

  removeRole(roleRow) {
    if (document.querySelectorAll('.role-budget-row').length > 1) {
      roleRow.remove();
      this.calculateBudget();
    }
  }

  addAnniversary() {
    const yearInput = document.getElementById('add-anniversary-year');
    const year = parseInt(yearInput.value);
    
    if (year && year >= 1) {
      const anniversaryPoints = document.getElementById('anniversary-points');
      const newAnniversary = document.createElement('div');
      newAnniversary.className = 'row mb-2';
      const pointsValue = year * 100;
      const costValue = this.formatCurrency(pointsValue / 100); // Default 100:1 ratio
      newAnniversary.innerHTML = `
        <div class="col-md-2">
          <label class="form-label small">${year} Years</label>
        </div>
        <div class="col-md-2">
          <input type="number" class="form-control" value="${pointsValue}" min="0" data-anniversary-year="${year}">
          <small class="text-muted">Points</small>
        </div>
        <div class="col-md-2">
          <input type="number" class="form-control" value="10" min="0" max="100" step="0.1" data-anniversary-percentage="${year}">
          <small class="text-muted">% of employees</small>
        </div>
        <div class="col-md-2">
          <div class="form-control-plaintext" data-anniversary-employee-count="${year}">0</div>
          <small class="text-muted">Employees</small>
        </div>
        <div class="col-md-2">
          <div class="form-control-plaintext" data-anniversary-cost="${year}">${costValue}</div>
          <small class="text-muted">Cost per</small>
        </div>
        <div class="col-md-2">
          <button type="button" class="btn btn-outline-danger btn-sm remove-anniversary">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      `;
      anniversaryPoints.appendChild(newAnniversary);
      yearInput.value = '';
      this.updateAnniversaryEmployeeCounts();
      this.calculateBudget();
    }
  }

  getRoleBudgets() {
    const roles = [];
    document.querySelectorAll('.role-budget-row').forEach(row => {
      const name = row.querySelector('[data-role-name]').value.trim();
      const count = parseInt(row.querySelector('[data-role-count]').value) || 0;
      const points = parseInt(row.querySelector('[data-role-points]').value) || 0;
      
      if (name && count > 0) {
        roles.push({ name, count, points });
      }
    });
    return roles;
  }

  getAnniversaryData() {
    const anniversaries = {};
    document.querySelectorAll('[data-anniversary-year]').forEach(input => {
      const year = parseInt(input.getAttribute('data-anniversary-year'));
      const points = parseInt(input.value) || 0;
      const percentageInput = document.querySelector(`[data-anniversary-percentage="${year}"]`);
      const percentage = parseFloat(percentageInput.value) || 0;
      
      if (year && points > 0 && percentage > 0) {
        anniversaries[year] = {
          points,
          percentage
        };
      }
    });
    return anniversaries;
  }

  updateAnniversaryEmployeeCounts() {
    const totalEmployees = parseFloat(this.employeeCountInput.value) || 0;
    let totalPercentage = 0;
    let totalEmployeeCount = 0;
    
    document.querySelectorAll('[data-anniversary-percentage]').forEach(input => {
      const year = parseInt(input.getAttribute('data-anniversary-percentage'));
      const percentage = parseFloat(input.value) || 0;
      const employeeCount = Math.round((totalEmployees * percentage) / 100);
      
      totalPercentage += percentage;
      totalEmployeeCount += employeeCount;
      
      const employeeCountElement = document.querySelector(`[data-anniversary-employee-count="${year}"]`);
      if (employeeCountElement) {
        employeeCountElement.textContent = this.formatNumber(employeeCount);
      }
    });

    // Update totals
    const totalPercentageElement = document.getElementById('anniversary-percentage-total');
    const totalEmployeeElement = document.getElementById('anniversary-employee-total');
    
    if (totalPercentageElement) {
      totalPercentageElement.textContent = `${totalPercentage.toFixed(1)}%`;
      // Add visual indicator for percentage total
      totalPercentageElement.className = totalPercentage > 100 ? 
        'fw-bold text-center text-danger' : 
        totalPercentage === 100 ? 
          'fw-bold text-center text-success' : 
          'fw-bold text-center text-warning';
    }
    
    if (totalEmployeeElement) {
      totalEmployeeElement.textContent = this.formatNumber(totalEmployeeCount);
    }
  }

  updateAnniversaryCosts() {
    const pointsRatio = parseFloat(this.pointsRatioInput.value) || 100;
    
    // Update the header label
    const headerElement = document.getElementById('anniversary-cost-header');
    if (headerElement) {
      headerElement.textContent = `Cost per (at ${pointsRatio}:1 ratio)`;
    }
    
    document.querySelectorAll('[data-anniversary-year]').forEach(input => {
      const year = parseInt(input.getAttribute('data-anniversary-year'));
      const points = parseInt(input.value) || 0;
      const costElement = document.querySelector(`[data-anniversary-cost="${year}"]`);
      if (costElement) {
        const cost = points / pointsRatio;
        costElement.textContent = this.formatCurrency(cost);
      }
    });
  }

  formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  formatNumber(value) {
    return new Intl.NumberFormat('en-US').format(value);
  }

  updateRoleBudgetChart(roles) {
    const chartData = roles.map(role => ({
      name: role.name,
      y: role.count * role.points * 12, // Annual points
      count: role.count,
      points: role.points
    }));

    if (this.roleBudgetChart) {
      this.roleBudgetChart.destroy();
    }

    this.roleBudgetChart = Highcharts.chart('role-budget-chart', {
      chart: {
        type: 'pie',
        height: 300
      },
      title: {
        text: 'Annual Points by Role'
      },
      tooltip: {
        pointFormat: '<b>{point.name}</b><br/>' +
                    'Employees: {point.count}<br/>' +
                    'Points/month: {point.points}<br/>' +
                    'Annual points: {point.y:,.0f}<br/>' +
                    'Percentage: {point.percentage:.1f}%'
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b><br/>{point.percentage:.1f}%'
          },
          showInLegend: true
        }
      },
      series: [{
        name: 'Annual Points',
        colorByPoint: true,
        data: chartData
      }]
    });
  }

  updateAnniversaryChart(anniversaries, totalEmployees) {
    const pointsRatio = parseFloat(this.pointsRatioInput.value) || 100;
    const chartData = Object.entries(anniversaries).map(([year, data]) => {
      const employeeCount = Math.round((totalEmployees * data.percentage) / 100);
      const totalPoints = data.points * employeeCount;
      const totalCost = totalPoints / pointsRatio;
      
      return {
        name: `${year} Years`,
        y: totalPoints,
        employeeCount,
        pointsPerEmployee: data.points,
        percentage: data.percentage,
        totalCost: this.formatCurrency(totalCost)
      };
    });

    if (this.anniversaryChart) {
      this.anniversaryChart.destroy();
    }

    this.anniversaryChart = Highcharts.chart('anniversary-chart', {
      chart: {
        type: 'column',
        height: 300
      },
      title: {
        text: 'Annual Anniversary Points by Years of Service'
      },
      xAxis: {
        type: 'category',
        labels: {
          rotation: -45,
          style: {
            fontSize: '12px'
          }
        }
      },
      yAxis: {
        title: {
          text: 'Total Points Awarded Annually'
        }
      },
      tooltip: {
        pointFormat: '<b>{point.name}</b><br/>' +
                    'Employees: {point.employeeCount:,.0f} ({point.percentage}%)<br/>' +
                    'Points per employee: {point.pointsPerEmployee:,.0f}<br/>' +
                    'Total annual points: {point.y:,.0f}<br/>' +
                    'Total annual cost: {point.totalCost}'
      },
      plotOptions: {
        column: {
          dataLabels: {
            enabled: true,
            format: '{point.y:,.0f}'
          }
        }
      },
      series: [{
        name: 'Anniversary Points',
        data: chartData,
        color: '#4E0090'
      }]
    });
  }

  calculateBudget() {
    const employeeCount = parseFloat(this.employeeCountInput.value) || 0;
    const redemptionRate = parseFloat(this.redemptionRateInput.value) || 0.6;
    const pointsRatio = parseFloat(this.pointsRatioInput.value) || 100;
    const programDuration = parseFloat(this.programDurationInput.value) || 24;

    // Update anniversary costs and employee counts display
    this.updateAnniversaryCosts();
    this.updateAnniversaryEmployeeCounts();

    // Get role-based budgets
    const roles = this.getRoleBudgets();
    const totalEmployeesFromRoles = roles.reduce((sum, role) => sum + role.count, 0);
    
    // Calculate monthly points from roles
    const monthlyRolePoints = roles.reduce((sum, role) => {
      return sum + (role.count * role.points);
    }, 0);
    
    // Calculate annual role points
    const annualRolePoints = monthlyRolePoints * 12;

    // Get anniversary data
    const anniversaries = this.getAnniversaryData();
    
    // Calculate anniversary costs based on actual employee percentages
    let totalAnniversaryPoints = 0;
    const anniversaryBreakdown = [];
    
    Object.entries(anniversaries).forEach(([year, data]) => {
      const employeeCountForYear = Math.round((employeeCount * data.percentage) / 100);
      const yearlyPoints = employeeCountForYear * data.points;
      totalAnniversaryPoints += yearlyPoints;
      anniversaryBreakdown.push(`${year}yr: ${this.formatNumber(employeeCountForYear)} × ${this.formatNumber(data.points)} = ${this.formatNumber(yearlyPoints)} pts`);
    });

    // Calculate total points awarded per year
    const totalPointsPerYear = annualRolePoints + totalAnniversaryPoints;
    
    // Calculate points redeemed per year (based on redemption rate)
    const pointsRedeemedPerYear = totalPointsPerYear * redemptionRate;
    
    // Calculate reward costs (points redeemed converted to currency)
    const rewardCosts = pointsRedeemedPerYear / pointsRatio;
    
    // Calculate platform costs ($3 per employee per month)
    const platformCostsPerMonth = employeeCount * 3;
    const platformCostsPerYear = platformCostsPerMonth * 12;
    
    // Calculate administration costs ($0.50 per employee per month)
    const adminCostsPerMonth = employeeCount * 0.50;
    const adminCostsPerYear = adminCostsPerMonth * 12;
    
    // Calculate one-time setup costs ($7.50 per employee, amortized over program duration)
    const setupCostsPerEmployee = 7.50;
    const totalSetupCosts = employeeCount * setupCostsPerEmployee;
    const annualSetupCosts = totalSetupCosts / (programDuration / 12);
    
    // Calculate total annual budget
    const totalAnnualBudget = rewardCosts + platformCostsPerYear + adminCostsPerYear + annualSetupCosts;
    
    // Calculate per-employee annual cost
    const costPerEmployee = totalAnnualBudget / employeeCount;
    
    // Calculate monthly budget
    const monthlyBudget = totalAnnualBudget / 12;

    // Calculate role breakdown
    const roleBreakdown = roles.map(role => 
      `${role.name}: ${this.formatNumber(role.count)} × ${this.formatNumber(role.points)} pts = ${this.formatNumber(role.count * role.points * 12)} pts/year`
    ).join(', ');

    // Update display
    this.budgetAmount.textContent = this.formatCurrency(totalAnnualBudget);
    this.budgetText.innerHTML = `
      Based on ${employeeCount.toLocaleString()} employees with ${(redemptionRate * 100).toFixed(0)}% redemption rate.
      <br><small class="text-muted">
        Monthly budget: ${this.formatCurrency(monthlyBudget)} | 
        Per employee: ${this.formatCurrency(costPerEmployee)}/year | 
        Total points: ${this.formatNumber(totalPointsPerYear)}/year
      </small>
      <br><small class="text-muted">
        Roles: ${roleBreakdown}
      </small>
      ${anniversaryBreakdown.length > 0 ? `<br><small class="text-muted">Anniversaries: ${anniversaryBreakdown.join(', ')}</small>` : ''}
    `;

    // Update charts
    this.updateRoleBudgetChart(roles);
    this.updateAnniversaryChart(anniversaries, employeeCount);
  }


  reset() {
    this.form.reset();
    this.calculateBudget();
  }
}

export default PointsBudgetCalculator; 
